import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { alerts } from "@features/alerts/alerts"
import { useFerve } from "@features/ferve/FerveProvider"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { trackEvent } from "@features/tracking/tracking"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, useBlockContext, useInlineEditableValue } from "eddev/blocks"
import { useState } from "react"

export const meta: BlockMeta = {
  title: "Ferve Pass",
  category: "embed",
  description: "A tile for a Ferve Pass",
  tags: ["#pass"],
  blockStyles: [
    { name: "default", label: "Default", isDefault: true },
    { name: "alt", label: "Alt" },
  ],
}

export default defineBlock("tickets/flexipass-tile", (props) => {
  const ferve = useFerve()
  const carpet = useFlyingCarpet()
  const extraLabel = useInlineEditableValue("extra", "")
  const readOnly = !env.admin || useBlockContext()?.readonly

  const [adding, setAdding] = useState(false)

  const [passTitle] = useInlineEditableValue("title")

  return (
    <div
      className={cn(
        "stack-y-4 md:stack-y-4 p-5 h-full",
        props.blockStyle === "alt"
          ? "theme-dark bg-bg text-white"
          : "bg-orange text-fg is-theme-dark:theme-light is-theme-dark:bg-white",
      )}
    >
      {/* Inner */}
      <div className="stack-y-4 flex-1">
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-1">
          <Chip variant="primary">
            <EditableText store="label" placeholder="Save $265" plainText />
          </Chip>
          {(!readOnly || extraLabel[0]) && (
            <Chip variant="inverted">
              <EditableText store={extraLabel} placeholder="Extra Tag" plainText />
            </Chip>
          )}
        </div>

        {/* Title and Price */}
        <div className="mt-2 stack-y-3 md:stack-y-2">
          <EditableText as="h3" store="title" className="type-label-caps" placeholder="Flexipass" plainText />
          <EditableText store="price" as="div" className="type-title-xl" placeholder="$100" plainText />
        </div>

        {/* Description */}
        <EditableText
          store="description"
          as="p"
          className="type-body-m nice-copy"
          placeholder="Laborum sit eiusmod nulla nisi nostrud eiusmod sit in aute ullamco."
        />
      </div>

      {/* Button */}
      <div className="mt-2 md:mt-4">
        <Button
          variant="primary"
          shape="branded"
          className="w-full"
          onClick={async (e) => {
            setAdding(true)
            try {
              const pricing = await ferve.cart.getPricing(props.ferveHash!)
              if (!pricing.length) {
                throw new Error("No pricing found")
              }
              const item = pricing[0]
              await ferve.cart.addItemsToCart(item.itemHash, [
                {
                  quantity: 1,
                  type: {
                    codeHash: item.codeHash,
                    priceTypeHash: item.priceTypeHash,
                  },
                },
              ])
              carpet.launch("cart")
              trackEvent(
                "Add Flexipass to Cart",
                {
                  PassType: passTitle,
                },
                e.target as HTMLButtonElement,
              )
            } catch (err) {
              alerts.displayAlert({
                title: "Error adding to cart",
                description: (err as any).message,
                confirmButton: "Okay",
              })
            } finally {
              setAdding(false)
            }
          }}
          loading={adding}
        >
          Add to Cart
        </Button>
      </div>
    </div>
  )
})
